import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Stack,
    Typography
} from '@mui/material';
import api from '../services/api';
import { ICliente, IProduto } from '../interfaces';
import { Autocomplete, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText } from '@mui/material';

const TenantProducts: React.FC = () => {
    const [tenants, setTenants] = useState<ICliente[]>([]);
    const [products, setProducts] = useState<IProduto[]>([]);
    const [open, setOpen] = useState(false);
    const [currentTenant, setCurrentTenant] = useState<ICliente | null>(null);
    const [selectedProducts, setSelectedProducts] = useState<IProduto[]>([]);
    const [productConfig, setProductConfig] = useState<Record<string, { modules: string[]; selectedTier?: number }>>({});

    const fetchData = async () => {
        try {
            const t = await api.get('/api/tenants/list');
            setTenants(t.data || []);
            const p = await api.get('/api/products');
            setProducts(p.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleOpen = (tenant: ICliente) => {
        setCurrentTenant(tenant);
        const sel = products.filter(p => (tenant.products || []).includes(p._id));
        setSelectedProducts(sel);
        // init config for selected products
        const cfg: Record<string, { modules: string[]; selectedTier?: number }> = {};
        sel.forEach(p => {
            cfg[p._id] = {
                modules: [],
                selectedTier: p.tiers && p.tiers.length > 0 ? p.tiers[0].rangeStart : undefined
            };
        });
        setProductConfig(cfg);
        setOpen(true);
    };

    const handleSave = async () => {
        if (!currentTenant) return;
        try {
            // For each selected product, call product link endpoint with selected tier and modules
            for (const p of selectedProducts) {
                const cfg = productConfig[p._id] || { modules: [], selectedTier: undefined };
                const body: any = {
                    productId: p._id,
                    clientId: currentTenant.tenantId,
                    selectedTier: cfg.selectedTier
                };
                if (cfg.modules && cfg.modules.length > 0) body.modules = cfg.modules;
                await api.post('/api/products/link', body);
            }
            await fetchData();
            setOpen(false);
        } catch (err) {
            console.error('Erro ao vincular produtos:', err);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>Associação de Produtos por Cliente</Typography>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Razão Social</TableCell>
                        <TableCell>Nome Fantasia</TableCell>
                        <TableCell>Produtos</TableCell>
                        <TableCell>Ações</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tenants.map(t => (
                        <TableRow key={t._id}>
                            <TableCell>{t.razaoSocial}</TableCell>
                            <TableCell>{t.nomeFantasia}</TableCell>
                            <TableCell>
                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                    {((t.products || []) as string[]).map(pid => {
                                        const prod = products.find(p => p._id === pid);
                                        return <Chip key={pid} label={prod ? prod.name : pid} size="small" />;
                                    })}
                                </Stack>
                            </TableCell>
                            <TableCell>
                                <Button variant="outlined" onClick={() => handleOpen(t)}>Associar</Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Associar Produtos</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        multiple
                        options={products}
                        getOptionLabel={opt => opt.name}
                        value={selectedProducts}
                        onChange={(_, value) => {
                            setSelectedProducts(value);
                            // initialize config for any newly selected products
                            const cfg = { ...productConfig };
                            value.forEach((p: IProduto) => {
                                if (!cfg[p._id]) {
                                    cfg[p._id] = { modules: [], selectedTier: p.tiers && p.tiers.length > 0 ? p.tiers[0].rangeStart : undefined };
                                }
                            });
                            setProductConfig(cfg);
                        }}
                        renderInput={(params) => <TextField {...params} label="Produtos" placeholder="Selecione produtos" />}
                    />

                    {selectedProducts.map((p) => (
                        <Box key={p._id} sx={{ mt: 2, mb: 2, border: '1px solid #eee', p: 2, borderRadius: 1 }}>
                            <Typography variant="subtitle1">{p.name}</Typography>
                            <FormControl fullWidth sx={{ mt: 1 }}>
                                <InputLabel id={`tier-label-${p._id}`}>Tier</InputLabel>
                                <Select
                                    labelId={`tier-label-${p._id}`}
                                    value={productConfig[p._id]?.selectedTier ?? ''}
                                    label="Tier"
                                    onChange={(e) => {
                                        const val = Number(e.target.value);
                                        setProductConfig(prev => ({ ...prev, [p._id]: { ...(prev[p._id] || { modules: [] }), selectedTier: val } }));
                                    }}
                                >
                                    {p.tiers && p.tiers.length > 0 ? p.tiers.map((t, i) => (
                                        <MenuItem key={i} value={t.rangeStart}>{`${t.rangeStart} - ${t.rangeEnd} (R$ ${t.price.toFixed(2)})`}</MenuItem>
                                    )) : <MenuItem value="">Sem tiers</MenuItem>}
                                </Select>
                            </FormControl>

                            <Autocomplete
                                multiple
                                options={p.modulos || []}
                                getOptionLabel={(opt: any) => opt.name || opt.description || ''}
                                value={(productConfig[p._id]?.modules || []).map(mid => p.modulos?.find(m => m.name === mid)!).filter(Boolean)}
                                onChange={(_, value) => {
                                    const mods = value.map((m: any) => m.name || m.description);
                                    setProductConfig(prev => ({ ...prev, [p._id]: { ...(prev[p._id] || { selectedTier: undefined }), modules: mods } }));
                                }}
                                renderInput={(params) => <TextField {...params} label="Módulos" placeholder="Selecione módulos" sx={{ mt: 1 }} />}
                                renderOption={(props, option) => (
                                    <li {...props}>{option.name || option.description}</li>
                                )}
                            />
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave}>Salvar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TenantProducts;
